import React from 'react';
import { CaptureSequence, Landmark } from './types';
import GridOverlay from './GridOverlay';
import GhostOverlay from './GhostOverlay';
import LevelIndicator from './LevelIndicator';

/**
 * S3b: this layer now carries only the guides that are *meant* to sit on the frame — grid, ghost,
 * level, plumb line, stability border. Capture coaching moved out to the docked info bar; it used to
 * float at `absolute bottom-20`, over the subject's feet.
 */
interface CaptureOverlayProps {
  currentView: CaptureSequence | null;
  landmarks: Landmark[] | null;
  isStable: boolean;
  showGrid?: boolean;
  showGhost?: boolean;
  showLevel?: boolean;
  showPlumbLine?: boolean;
  gridType?: '3x3' | 'golden' | 'center' | 'none';
  gridOpacity?: number;
  ghostOpacity?: number;
  plumbLineStyle?: 'solid' | 'dashed' | 'dotted';
}

const CaptureOverlay: React.FC<CaptureOverlayProps> = ({
  currentView,
  landmarks,
  isStable,
  showGrid = true,
  showGhost = true,
  showLevel = true,
  showPlumbLine = true,
  gridType = '3x3',
  gridOpacity = 0.4,
  ghostOpacity = 0.3,
  plumbLineStyle = 'dashed',
}) => {
  if (!currentView) return null;

  // Determine plumb line dash pattern
  const getPlumbLineDash = () => {
    switch (plumbLineStyle) {
      case 'solid': return 'none';
      case 'dotted': return '4 4';
      case 'dashed':
      default: return '12 8';
    }
  };

  return (
    <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden rounded-lg">
      {showGrid && gridType !== 'none' && <GridOverlay type={gridType} opacity={gridOpacity} />}
      {showGhost && <GhostOverlay view={currentView} opacity={ghostOpacity} />}
      {showLevel && <LevelIndicator />}
      
      {/* Plumb Line - vertical center reference */}
      {showPlumbLine && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.7 }}>
          <line
            x1="50%"
            y1="0"
            x2="50%"
            y2="100%"
            stroke="#fbbf24"
            strokeWidth="2"
            strokeDasharray={getPlumbLineDash()}
          />
        </svg>
      )}
      
      {/* Stability Border Effect */}
      <div 
        className={`absolute inset-0 border-4 transition-colors duration-300 pointer-events-none rounded-lg
          ${isStable ? 'border-success/60' : 'border-transparent'}
        `}
      />
    </div>
  );
};

export default CaptureOverlay;

